import React, { useEffect, useState } from"react";
import axios from"axios";
import API_BASE_URL from"../Config/config";
import {
 Box,
 Typography,
 Table,
 TableHead,
 TableRow,
 TableCell,
 TableBody,
 Button,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 Chip,
 Alert
} from"@mui/material";
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';

const AdminUsersDashboard = () => {
 const [users, setUsers] = useState([]);
 const [loading, setLoading] = useState(true);
 const [openDialog, setOpenDialog] = useState(false);
 const [openUnlockDialog, setOpenUnlockDialog] = useState(false);
 const [selectedUserId, setSelectedUserId] = useState(null);
 const [selectedUserForUnlock, setSelectedUserForUnlock] = useState(null);
 const [unlocking, setUnlocking] = useState(false);
 const [unlockSuccess, setUnlockSuccess] = useState(false);
 const [unlockError, setUnlockError] = useState('');
 const [showOnlyLocked, setShowOnlyLocked] = useState(true);

 const fetchUsers = async () => {
 try {
 const res = await axios.get(`${API_BASE_URL}/user/users`);
 setUsers(res.data);
 setLoading(false);
 } catch (error) {
 console.error("Error fetching users:", error);
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchUsers();
 }, []);

 // ✅ Open custom dialog
 const handleOpenDialog = (id) => {
 setSelectedUserId(id);
 setOpenDialog(true);
 };

 // ✅ Close dialog
 const handleCloseDialog = () => {
 setOpenDialog(false);
 setSelectedUserId(null);
 };

 // ✅ Confirm delete
 const handleConfirmDelete = async () => {
 try {
 await axios.delete(`${API_BASE_URL}/user/users/${selectedUserId}`);
 fetchUsers(); // Refresh after delete
 handleCloseDialog();
 } catch (error) {
 console.error("Error deleting user:", error);
 }
 };

 // ✅ Open unlock dialog
 const handleOpenUnlockDialog = (user) => {
 setSelectedUserForUnlock(user);
 setOpenUnlockDialog(true);
 setUnlockError('');
 setUnlockSuccess(false);
 };

 // ✅ Close unlock dialog
 const handleCloseUnlockDialog = () => {
 setOpenUnlockDialog(false);
 setSelectedUserForUnlock(null);
 setUnlockError('');
 setUnlockSuccess(false);
 };

 // ✅ Confirm unlock
 const handleConfirmUnlock = async () => {
 if (!selectedUserForUnlock) return;
 
 setUnlocking(true);
 setUnlockError('');
 setUnlockSuccess(false);

 try {
 const adminUserId = localStorage.getItem('user_id');
 const response = await axios.post(`${API_BASE_URL}/user/unblock`, {
 userId: selectedUserForUnlock.user_id,
 adminUserId: adminUserId
 });

 if (response.data && response.data.message) {
 setUnlockSuccess(true);
 // Refresh users list after a short delay
 setTimeout(() => {
 fetchUsers();
 handleCloseUnlockDialog();
 }, 1500);
 }
 } catch (error) {
 const errorMsg = error.response?.data?.error || 
 error.response?.data?.message || 
 error.message || 
 'Failed to unlock user account.';
 setUnlockError(errorMsg);
 } finally {
 setUnlocking(false);
 }
 };

 // Filter locked users
 const lockedUsers = users.filter(user => user.is_locked === true);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "var(--bg-main)", color: "var(--text-body)", p: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: "var(--primary-blue)", mb: 0, fontWeight: "extrabold", textTransform: "uppercase", letterSpacing: 1 }}>
          Admin <span style={{ color: "var(--secondary-blue)" }}>Users</span> Dashboard
        </Typography>

        {/* Toggle: show only locked users */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "var(--text-muted)", fontWeight: "bold" }}>
            FILTER:
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setShowOnlyLocked(prev => !prev)}
            sx={{
              backgroundColor: showOnlyLocked ? "var(--primary-blue)" : "var(--bg-section)",
              color: showOnlyLocked ? "#fff" : "var(--primary-blue)",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: 2,
              px: 3,
              py: 1,
              border: "1px solid var(--primary-blue)",
              "&:hover": {
                backgroundColor: showOnlyLocked ? "var(--secondary-blue)" : "var(--bg-soft)",
              },
            }}
          >
            {showOnlyLocked ? "Locked only" : "All users"}
          </Button>
        </Box>
      </Box>

 {/* Locked Users Alert */}
 {lockedUsers.length > 0 && (
 <Alert 
 severity="warning" 
 sx={{ 
 mb: 4, 
 backgroundColor:"rgba(124, 58, 237, 0.1)", 
 color:"#7C3AED",
 border:"1px solid rgba(124, 58, 237, 0.2)",
 borderRadius: 3,
"& .MuiAlert-icon": { color:"#7C3AED" }
 }}
 >
 <strong>{lockedUsers.length} account(s) locked</strong> - Review and unlock below to restore access.
 </Alert>
 )}

      <Box
        sx={{
          borderRadius: 4,
          boxShadow: "0 20px 50px rgba(31, 138, 203, 0.15)",
          overflowX: "auto",
          border: "1px solid var(--accent-gray)",
          backgroundColor: "#FFFFFF",
        }}
      >
        {loading ? (
          <Box sx={{ p: 8, textAlign: "center" }}>
            <Typography sx={{ color: "var(--primary-blue)", animate: "pulse" }}>Loading user database...</Typography>
          </Box>
        ) : (
          <Table sx={{ backgroundColor: "transparent" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "var(--bg-header-footer)" }}>
                <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>ID</TableCell>
                <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>First Name</TableCell>
                <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>Last Name</TableCell>
                <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>Email</TableCell>
                <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>User ID</TableCell>
                <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>College</TableCell>
                <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>Status</TableCell>
                <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(showOnlyLocked ? lockedUsers : users).map((user, index) => (
                <TableRow 
                  key={user.id}
                  sx={{ 
                    "&:hover": { backgroundColor: "var(--bg-main)" }
                  }}
                >
                  <TableCell sx={{ color: "var(--text-muted)", borderBottom: "1px solid var(--accent-gray)" }}>{index + 1}</TableCell>
                  <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>{user.first_name}</TableCell>
                  <TableCell sx={{ color: "var(--text-heading)", fontWeight: "bold", borderBottom: "1px solid var(--accent-gray)" }}>{user.last_name}</TableCell>
                  <TableCell sx={{ color: "var(--text-body)", borderBottom: "1px solid var(--accent-gray)" }}>{user.email}</TableCell>
                  <TableCell sx={{ color: "var(--text-body)", borderBottom: "1px solid var(--accent-gray)" }}>{user.user_id}</TableCell>
                  <TableCell sx={{ color: "var(--text-body)", borderBottom: "1px solid var(--accent-gray)" }}>{user.college_name || '-'}</TableCell>
                  <TableCell sx={{ borderBottom: "1px solid var(--accent-gray)" }}>
                    {user.is_locked ? (
                      <Chip
                        icon={<LockIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
                        label="Locked"
                        size="small"
                        sx={{ backgroundColor: "#ef4444", color: "#fff", fontWeight: "bold", borderRadius: 1.5 }}
                      />
                    ) : (
                      <Chip
                        icon={<LockOpenIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
                        label="Active"
                        size="small"
                        sx={{ backgroundColor: "#10b981", color: "#fff", fontWeight: "bold", borderRadius: 1.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid var(--accent-gray)" }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {user.is_locked && (
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ 
                            backgroundColor: "var(--primary-blue)", 
                            color: "#fff", 
                            fontWeight: "bold",
                            borderRadius: 2,
                            textTransform: "none",
                            "&:hover": { backgroundColor: "var(--secondary-blue)" }
                          }}
                          onClick={() => handleOpenUnlockDialog(user)}
                        >
                          Unlock
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ 
                          backgroundColor: "#ef4444", 
                          color: "#fff", 
                          fontWeight: "bold",
                          borderRadius: 2,
                          textTransform: "none",
                          "&:hover": { backgroundColor: "#dc2626" }
                        }}
                        onClick={() => handleOpenDialog(user.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

  {/* ✅ Delete Confirmation Dialog */}
  <Dialog
    open={openDialog}
    onClose={handleCloseDialog}
    PaperProps={{
      sx: {
        backgroundColor: "#FFFFFF",
        borderRadius: 4,
        border: "1px solid var(--accent-gray)",
        color: "var(--text-body)"
      }
    }}
  >
    <DialogTitle sx={{ color: "var(--primary-blue)", fontWeight: "bold" }}>Confirm Deletion</DialogTitle>
    <DialogContent sx={{ color: "var(--text-body)" }}>
      Are you sure you want to delete this user? This action cannot be undone.
    </DialogContent>
    <DialogActions sx={{ p: 3 }}>
      <Button onClick={handleCloseDialog} sx={{ color: "var(--text-muted)", fontWeight: "bold" }}>
        Cancel
      </Button>
      <Button
        onClick={handleConfirmDelete}
        variant="contained"
        sx={{ backgroundColor: "#ef4444", color: "#fff", fontWeight: "bold", borderRadius: 2, "&:hover": { backgroundColor: "#dc2626" } }}
      >
        Confirm Delete
      </Button>
    </DialogActions>
  </Dialog>

  {/* ✅ Unlock Confirmation Dialog */}
  <Dialog
    open={openUnlockDialog}
    onClose={handleCloseUnlockDialog}
    PaperProps={{
      sx: {
        backgroundColor: "#FFFFFF",
        borderRadius: 4,
        border: "1px solid var(--accent-gray)",
        color: "var(--text-body)"
      }
    }}
  >
    <DialogTitle sx={{ color: "var(--primary-blue)", fontWeight: "bold" }}>
      Unlock User Account
    </DialogTitle>
    <DialogContent sx={{ minWidth: '400px' }}>
      {unlockSuccess ? (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          Account unlocked successfully!
        </Alert>
      ) : unlockError ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {unlockError}
        </Alert>
      ) : null}

      {selectedUserForUnlock && (
        <Box>
          <Typography sx={{ mb: 2, color: "var(--text-body)" }}>
            Are you sure you want to unlock this account?
          </Typography>
          <Box sx={{ backgroundColor: "var(--bg-main)", p: 2, borderRadius: 2, mb: 2, border: "1px solid var(--accent-gray)" }}>
            <Typography variant="body2" sx={{ color: "var(--text-muted)", fontWeight: "bold", textTransform: "uppercase" }}>User ID:</Typography>
            <Typography sx={{ color: "var(--text-heading)", mb: 1, fontWeight: 600 }}>{selectedUserForUnlock.user_id}</Typography>
            <Typography variant="body2" sx={{ color: "var(--text-muted)", fontWeight: "bold", textTransform: "uppercase" }}>Name:</Typography>
            <Typography sx={{ color: "var(--text-heading)", fontWeight: 600 }}>{selectedUserForUnlock.first_name} {selectedUserForUnlock.last_name}</Typography>
          </Box>
        </Box>
      )}
    </DialogContent>
    <DialogActions sx={{ p: 3 }}>
      <Button onClick={handleCloseUnlockDialog} disabled={unlocking} sx={{ color: "var(--text-muted)", fontWeight: "bold" }}>
        Cancel
      </Button>
      <Button
        onClick={handleConfirmUnlock}
        variant="contained"
        disabled={unlocking || unlockSuccess}
        sx={{
          backgroundColor: "var(--primary-blue)",
          color: "#fff",
          fontWeight: "bold",
          borderRadius: 2,
          "&:hover": { backgroundColor: "var(--secondary-blue)" }
        }}
      >
        {unlocking ? 'Unlocking...' : 'Unlock Account'}
      </Button>
    </DialogActions>
  </Dialog>
 </Box>
 );
};

export default AdminUsersDashboard;



